/**
 * DirectoryRow — the ONE linked-row system for the directory detail
 * surfaces (Directory detail rebuild, founder-approved mockup §5). Used
 * in four places: Org → Contacts, Individual → Affiliations, and the
 * Connected-clients read on BOTH pages.
 *
 * Row anatomy: a 40px identity avatar (people/clients = forest circle with
 * serif cream initials; orgs = sage-50 rounded-square with a building
 * glyph in sage-600), a semibold sage-800 name (with an optional gold
 * primary star), a meta line whose parts divide on sage-300 middle dots,
 * rest-invisible Edit/Remove chrome, and a sage-300 chevron. The WHOLE row
 * is the tap target (a stretched Link — the name link's ::after covers the
 * row); action chips sit above it on their own stacking layer.
 *
 * Chrome-rest (design-language §7 U4): Edit/Remove rest at opacity-0 and
 * fade in on hover / focus-within (never display:none, so focus can reach
 * them). Touch pointers (@media (hover:none)) keep them always visible.
 * Remove's hover is the pepper wash.
 *
 * Presentational only — no data access, no gating; composers gate and pass
 * the handlers.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, Pencil, Plus, Star, X } from 'lucide-react';

/** First-two alpha initials of a display name (band + row avatars share this). */
export function initialsOf(name: string): string {
  return (
    (name || '')
      .split(/\s+/)
      .filter((w) => w && /[A-Za-z0-9]/.test(w[0]))
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || '?'
  );
}

export type DirectoryAvatarKind = 'person' | 'client' | 'org';

/** The 40px identity avatar (row scale). */
export function RowAvatar({ kind, name }: { kind: DirectoryAvatarKind; name: string }): ReactNode {
  if (kind === 'org') {
    return (
      <span
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-base bg-sage-50 text-sage-600"
      >
        <Building2 size={18} strokeWidth={1.8} />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-sage-800 font-display text-sm font-semibold text-cream"
    >
      {initialsOf(name)}
    </span>
  );
}

// Chrome-rest utility: rest invisible, fade in on hover/focus-within, and
// always-on for touch pointers (design-language §7 U4).
const REST_CHROME =
  'opacity-0 transition-opacity duration-[var(--dur-base)] group-hover:opacity-100 ' +
  'group-focus-within:opacity-100 [@media(hover:none)]:opacity-100';

export interface DirectoryRowProps {
  /** Detail-page route the whole row navigates to. */
  to: string;
  avatar: DirectoryAvatarKind;
  name: string;
  /** Gold filled star after the name (primary affiliation) — display only. */
  primary?: boolean;
  /**
   * Interactive primary-star toggle after the name (preserves the
   * set/clear-primary affordance). Sits above the stretched link.
   */
  starToggle?: { active: boolean; onToggle: () => void; busy?: boolean };
  /** Meta parts — joined by sage-300 middle dots. */
  meta?: ReactNode[];
  /** Archived / inactive → the whole row reads muted. */
  muted?: boolean;
  /** A trailing status chip (e.g. an "Archived" mini-chip) before the actions. */
  trailing?: ReactNode;
  onEdit?: () => void;
  onRemove?: () => void;
  /** Accessible label for the remove chip (names the entity). */
  removeAriaLabel?: string;
  editAriaLabel?: string;
  testId?: string;
}

export function DirectoryRow({
  to,
  avatar,
  name,
  primary = false,
  starToggle,
  meta,
  muted = false,
  trailing,
  onEdit,
  onRemove,
  removeAriaLabel,
  editAriaLabel,
  testId,
}: DirectoryRowProps): ReactNode {
  const metaParts = (meta ?? []).filter(Boolean);
  return (
    <div
      data-testid={testId}
      className={
        'group relative flex items-center gap-3 rounded-base px-3 py-2.5 transition-colors ' +
        'duration-[var(--dur-base)] hover:bg-sand focus-within:bg-sand ' +
        (muted ? 'opacity-60' : '')
      }
    >
      <RowAvatar kind={avatar} name={name} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {/* The stretched link — its ::after covers the whole row as the tap
              target; z-10 siblings (star toggle, action chips) stay above it. */}
          <Link
            to={to}
            title={name}
            className="min-w-0 truncate text-[14.5px] font-semibold text-sage-800 after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
          >
            {name}
          </Link>
          {starToggle ? (
            <button
              type="button"
              onClick={starToggle.onToggle}
              disabled={starToggle.busy}
              aria-pressed={starToggle.active}
              aria-label={starToggle.active ? 'Clear primary' : 'Set primary'}
              className={
                'relative z-10 flex shrink-0 items-center justify-center rounded-tight p-0.5 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                (starToggle.active ? 'text-warm-400' : 'text-sage-300 hover:text-warm-400')
              }
            >
              <Star
                size={14}
                aria-hidden="true"
                fill={starToggle.active ? 'currentColor' : 'none'}
                strokeWidth={starToggle.active ? 0 : 1.6}
              />
            </button>
          ) : (
            primary && (
              <Star
                size={13}
                aria-label="Primary affiliation"
                className="relative z-10 shrink-0 text-warm-400"
                fill="currentColor"
                strokeWidth={0}
              />
            )
          )}
        </div>
        {metaParts.length > 0 && (
          <div className="mt-0.5 flex flex-wrap items-center text-[12.5px] text-ink-soft">
            {metaParts.map((part, i) => (
              <span key={i} className="inline-flex items-center">
                {i > 0 && <span aria-hidden="true" className="mx-1.5 text-sage-300">·</span>}
                <span>{part}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {trailing && <span className="relative z-10 shrink-0">{trailing}</span>}

      {(onEdit || onRemove) && (
        <span className={'relative z-10 flex shrink-0 items-center gap-1 ' + REST_CHROME}>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              aria-label={editAriaLabel ?? 'Edit'}
              title={editAriaLabel ?? 'Edit'}
              className="flex h-8 w-8 items-center justify-center rounded-base text-sage-400 transition-colors hover:bg-sage-50 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeAriaLabel ?? 'Remove'}
              title={removeAriaLabel ?? 'Remove'}
              className="flex h-8 w-8 items-center justify-center rounded-base text-sage-400 transition-colors hover:bg-pepper-soft hover:text-pepper-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pepper-deep"
            >
              <X size={15} aria-hidden="true" />
            </button>
          )}
        </span>
      )}

      <ChevronRight
        size={16}
        aria-hidden="true"
        className="relative shrink-0 text-sage-300 transition-colors group-hover:text-sage-400"
      />
    </div>
  );
}

/**
 * DirectoryRowCard — THE card recipe wrapper for a linked-row list: the
 * card title carries a small count pill; rows divide on hairlines; the
 * footer holds an optional dashed-ghost add door and/or an explainer line.
 */
export interface DirectoryRowCardProps {
  title: string;
  count?: number;
  children: ReactNode;
  /** Dashed-ghost add affordance below the rows. */
  addLabel?: string;
  onAdd?: () => void;
  addTestId?: string;
  /** A quiet 12px ink-soft explainer under the rows (Connected clients). */
  explainer?: ReactNode;
}

export function DirectoryRowCard({
  title,
  count,
  children,
  addLabel,
  onAdd,
  addTestId,
  explainer,
}: DirectoryRowCardProps): ReactNode {
  return (
    <section className="rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh1 sm:p-5">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="font-display text-lg text-sage-800">{title}</h2>
        {typeof count === 'number' && count > 0 && (
          <span className="rounded-pill bg-sage-50 px-2 py-0.5 text-xs font-semibold text-sage-400">
            {count}
          </span>
        )}
      </div>
      <div className="divide-y divide-sage-800/[8%]">{children}</div>
      {explainer && <p className="mt-3 text-xs text-ink-soft">{explainer}</p>}
      {onAdd && addLabel && (
        <div className="mt-3">
          <DashedAdd label={addLabel} onClick={onAdd} testId={addTestId} />
        </div>
      )}
    </section>
  );
}

/**
 * DashedAdd — the dashed-border ghost add door (design-language: adding is
 * an action, never chip-weight content). Two sizes: `pill` for the chip
 * groups, `row` for the linked-row footers.
 */
export function DashedAdd({
  label,
  onClick,
  size = 'row',
  testId,
}: {
  label: string;
  onClick: () => void;
  size?: 'pill' | 'row';
  testId?: string;
}): ReactNode {
  const sizeCls =
    size === 'pill'
      ? 'rounded-tight px-2 py-0.5 text-[11px] font-medium'
      : 'rounded-base px-3 py-1.5 text-sm font-medium';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        'inline-flex items-center gap-1 border border-dashed border-sage-300 text-sage-400 transition-colors ' +
        'hover:border-sage-400 hover:bg-sage-50 hover:text-sage-600 ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
        sizeCls
      }
    >
      <Plus size={size === 'pill' ? 12 : 15} aria-hidden="true" />
      {label}
    </button>
  );
}
